import { useEffect } from 'react';
import { preloadFlags, isFlagCached, clearFlagCache } from '../utils/flagCache';

/**
 * Custom hook for managing flag cache operations
 */
export const useFlagCache = () => {
  /**
   * Preload a list of country flags
   * @param countryCodes Array of country codes to preload
   */
  const preloadCountryFlags = (countryCodes: string[]) => {
    const uniqueCodes = Array.from(new Set(countryCodes.filter(code => code?.trim())));
    if (uniqueCodes.length > 0) {
      preloadFlags(uniqueCodes);
    }
  };

  /**
   * Check if a specific flag is cached
   * @param countryCode Country code to check
   * @returns Boolean indicating if the flag is cached
   */
  const checkFlagCached = (countryCode: string): boolean => {
    return isFlagCached(countryCode);
  };

  /**
   * Clear all cached flags
   * Useful for memory management in long-running applications
   */
  const clearAllFlags = () => {
    clearFlagCache();
  };

  /**
   * Preload flags for common leaderboard countries
   * Call this when loading leaderboard data
   */
  const preloadLeaderboardFlags = (leaderboardData: Array<{ country?: string }>) => {
    const countryCodes = leaderboardData
      .map(entry => entry.country)
      .filter((code): code is string => !!code);
    preloadCountryFlags(countryCodes);
  };

  /**
   * Preload flags for user profiles
   * Call this when loading multiple user profiles
   */
  const preloadUserFlags = (users: Array<{ country?: string }>) => {
    const countryCodes = users
      .map(user => user.country)
      .filter((code): code is string => !!code);
    preloadCountryFlags(countryCodes);
  };

  return {
    preloadCountryFlags,
    checkFlagCached,
    clearAllFlags,
    preloadLeaderboardFlags,
    preloadUserFlags
  };
};

/**
 * Hook that automatically preloads flags when the component mounts
 * @param countryCodes Array of country codes to preload
 */
export const usePreloadFlags = (countryCodes: string[]) => {
  const { preloadCountryFlags } = useFlagCache();

  useEffect(() => {
    if (countryCodes.length > 0) {
      preloadCountryFlags(countryCodes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCodes.join(','), preloadCountryFlags]);
};

export default useFlagCache;
